import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { UserSubscriptionInfo, Course, CourseDetails } from "../../../core/data/interface";
import ThumbnailImage from "../../../core/common/thumbnailImage";
import CourseDetailsModal from "../courses/courseDetailsModal";
import PreLoader from "../../../core/common/preLoader";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders } from "../../../common/Utils";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { DEFAULT_THUMBNAIL_URL, API_URL } from "../../../environment";
import CustomToast from "../../../core/common/customToast";

const MyAccount = () => {
  const [data, setData] = useState<any>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState<number | null>(null);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  
  // API call setup
  const { isLoading, error, callFetch } = useFetch();

  const fetchAccountData = useCallback(() => {
    callFetch(
      API.COURSE_HOME_DATA,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response)=>{
      setData(response);
    }).catch((err) => {
      console.error('Failed to fetch account data:', err);
    });
  }, []);

  // Fetch account data on component mount
  useEffect(() => {
    fetchAccountData();
  }, []);

  // Handle API errors
  useEffect(() => {
    if (error) {
      setToastSettings({
        show: true,
        message: error.response?.data || "Something went wrong please try again later",
        heading: "Error",
        type: "ERROR",
      });
    }
  }, [error]);


  const downloadInvoice = async (subscriptionId: number) => {
    setDownloadingInvoice(subscriptionId);
    
    try {
      const headers = getAPIHeaders();
      const response = await fetch(
        `${API_URL}/${API.DOWNLOAD_SUBSCRIPTION_INVOICE.replace(':subscriptionId', subscriptionId.toString())}`,
        {
          method: 'GET',
          headers: headers as any,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${subscriptionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      setToastSettings({
        show: true,
        message: "Failed to download invoice. Please try again.",
        heading: "Error",
        type: "ERROR",
      });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getSubscriptionEnrolledCourse = (subscriptionPlanId: number): Course | null => {
    if (!data?.courseCollections) return null;
    const allCourses = data.courseCollections.enrolled_courses || []
    for (const course of allCourses) {
      const subscriptions = (course.availableSubscriptions as any[]) || [];
      const hasMatchingSubscription = subscriptions.some(
        (sub: any) => sub.subscriptionPlanId === subscriptionPlanId
      );
      if (hasMatchingSubscription) {
        return course;
      }
    }
    return null;
  };

  // Get user info with null safety
  const userInfo: UserSubscriptionInfo | null = data?.userSubscriptionInfo || null;

  // Helper function to format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time spent
  const formatTimeSpent = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  // Helper function to get subscription plan details from enrolled courses
  const getSubscriptionPlanDetails = (subscriptionId: number) => {
    if (!data?.courseCollections) return null;
    
    const allCourses = [
      ...(data.courseCollections.available_courses || []),
      ...(data.courseCollections.enrolled_courses || [])
    ];
    
    for (const course of allCourses) {
      const subscriptions = course.availableSubscriptions as any[];
      const subscription = subscriptions.find((sub: any) => 
        sub.subscriptionPlanId === subscriptionId && 'userEnrollmentDetails' in sub
      );
      if (subscription) {
        return subscription;
      }
    }
    return null;
  };

  const countStudyMaterials = (courseDetails?: CourseDetails) => {
    const counts = { videos: 0, documents: 0, links: 0 };

    if (!courseDetails?.subjects?.length) {
      return counts;
    }

    courseDetails.subjects.forEach((subject) => {
      subject?.syllabus?.forEach((syllabus) => {
        syllabus?.studyMaterials?.forEach((material) => {
          if (!material) return;

          if (material.materialType === "VIDEO") {
            counts.videos += 1;
            return;
          }

          if (material.materialType === "NOTE" || material.materialType === "FILE") {
            counts.documents += 1;
            return;
          }

          if (material.materialType === "LINK") {
            const content = material.content || "";
            if (
              content.includes("youtube.com") ||
              content.includes("youtu.be") ||
              content.includes("m.youtube.com")
            ) {
              counts.videos += 1;
            } else {
              counts.links += 1;
            }
          }
        });
      });
    });

    return counts;
  };

  const calculateCourseProgress = (courseDetails?: CourseDetails) => {
    if (!courseDetails?.subjects?.length) {
      return null;
    }

    let totalMaterials = 0;
    let totalProgress = 0;

    courseDetails.subjects.forEach((subject) => {
      subject?.syllabus?.forEach((syllabus) => {
        syllabus?.studyMaterials?.forEach((material) => {
          if (!material) return;

          totalMaterials += 1;
          const completionStatus =
            material.userEntityInteraction?.completionStatus ||
            (material as any)?.completionStatus;
          const calculatedProgress =
            typeof material.userEntityInteraction?.progressPercentage === "number"
              ? material.userEntityInteraction.progressPercentage
              : typeof (material as any)?.progressPercentage === "number"
              ? (material as any).progressPercentage
              : 0;
          const materialProgress =
            completionStatus === "COMPLETED" ? 100 : calculatedProgress;

          totalProgress += materialProgress;
        });
      });
    });

    if (totalMaterials === 0) {
      return null;
    }

    const averageProgress = totalProgress / totalMaterials;
    const normalizedProgress = Math.min(100, Math.max(0, averageProgress));

    return Number(normalizedProgress.toFixed(1));
  };

  // Show error state
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
              <h4>Failed to load account information</h4>
              <p className="text-muted">Please try again later.</p>
              <button className="btn btn-primary" onClick={fetchAccountData}>
                <i className="ti ti-refresh me-2"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no data is available
  if (!isLoading && (!data || !userInfo)) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-user-off display-1 text-muted mb-3"></i>
              <h4>No account information available</h4>
              <p className="text-muted">Unable to load your account details at the moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      <PreLoader showLoader={isLoading} />
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">My Account</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    My Account
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="container-fluid d-flex justify-content-center">
            <div className="w-md-80">

        {/* My Active Subscriptions */}
        <div className="mb-5">
          <h4 className="mb-4">My On Going Batches</h4>
          {userInfo?.activeSubscriptions && userInfo?.activeSubscriptions.length > 0 ? (
            userInfo.activeSubscriptions.map((subscription) => {
            const planDetails = getSubscriptionPlanDetails(subscription.subscriptionPlanId);
            const enrolledCourse = getSubscriptionEnrolledCourse(subscription.subscriptionPlanId) 
            return (
              <div key={subscription.subscriptionPlanId} className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-3">{subscription.planName}</h5>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`badge ${subscription.status === 'ACTIVE' ? 'bg-success' : 'bg-warning'}`}>
                      {subscription.status}
                    </span>
                    <button 
                      hidden={subscription.planType === 'FREE' || subscription.planType === 'DEFAULT'}
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => downloadInvoice(subscription.subscriptionPlanId)}
                      disabled={downloadingInvoice === subscription.subscriptionPlanId}
                    >
                      {downloadingInvoice === subscription.subscriptionPlanId ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-download me-2"></i>
                          Download Invoice
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Batch Type:</span>
                        <span className="fw-medium">{subscription.planType}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Start Date:</span>
                        <span className="fw-medium">{formatDate(subscription.enrollmentDate)}</span>
                      </div>
                      {subscription.expiryDate && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Expiry Date:</span>
                          <span className="fw-medium">{formatDate(subscription.expiryDate)}</span>
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      {planDetails && 'duration' in planDetails && (
                        <>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Duration:</span>
                            <span className="fw-medium">{planDetails.duration === 0 ? 'Lifetime' : `${planDetails.duration} months`}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Fees Paid:</span>
                            <span className="fw-medium text-success">{planDetails.pricing.currencyCode} {planDetails.pricing.promotionalPrice}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Included Courses */}
                  <div className="mt-3 p-3 bg-light rounded">
                    <h6 className="mb-2">Included Courses:</h6>
                    <div className="d-flex flex-wrap gap-2">
                        {enrolledCourse && (
                          <span key={enrolledCourse.courseId} className="badge bg-primary">
                            {enrolledCourse.courseTitle}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
          ) : (
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="ti ti-credit-card display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No Active Batches</h5>
                <p className="text-muted">You are not enrolled in any batches at the moment.</p>
              </div>
            </div>
          )}
        </div>

        {/* Course Progress & Learning Overview */}
        <div className="mb-5">
          <h4 className="mb-4">Course Progress & Learning Overview</h4>
          
                  {/* Learning Statistics */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-4">Learning Statistics</h5>
                      <div className="row">
                        <div className="col mb-3">
                          <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                            <i className="ti ti-book text-primary mb-2" style={{fontSize: '2rem'}}></i>
                            <div className="h4 text-primary mb-1">{userInfo?.stats?.totalCoursesAccessible || 0}</div>
                            <div className="text-muted small">Total Courses</div>
                          </div>
                        </div>
                        <div className="col mb-3">
                          <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                            <i className="ti ti-clock text-success mb-2" style={{fontSize: '2rem'}}></i>
                            <div className="h4 text-success mb-1">{userInfo?.stats?.coursesStarted || 0}</div>
                            <div className="text-muted small">Course Started</div>
                          </div>
                        </div>
                        <div className="col mb-3">
                          <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                            <i className="ti ti-check text-info mb-2" style={{fontSize: '2rem'}}></i>
                            <div className="h4 text-info mb-1">{userInfo?.stats?.coursesCompleted || 0}</div>
                            <div className="text-muted small">Course Completed</div>
                          </div>
                        </div>
                        <div className="col mb-3">
                          <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                            <i className="ti ti-clock text-warning mb-2" style={{fontSize: '2rem'}}></i>
                            <div className="h4 text-warning mb-1">{formatTimeSpent(userInfo?.stats?.totalTimeSpentMinutes || 0)}</div>
                            <div className="text-muted small">Time Spent</div>
                          </div>
                        </div>
                        <div className="col mb-3">
                          <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                            <i className="ti ti-trending-up text-secondary mb-2" style={{fontSize: '2rem'}}></i>
                            <div className="h4 text-secondary mb-1">{(userInfo?.stats?.avgProgressPercentage || 0).toFixed(1)}%</div>
                            <div className="text-muted small">Avg Progress</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

          {/* Course Progress List */}
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-4">
                <i className="ti ti-bookmark text-primary me-2"></i>
                Your Course Progress
              </h5>
              {userInfo?.startedCourses && userInfo?.startedCourses?.length > 0 ? (
                userInfo?.startedCourses.map((course) => {
                const allCourses = [
                  ...(data.courseCollections?.available_courses || []),
                  ...(data.courseCollections?.enrolled_courses || [])
                ];
                const fullCourse = allCourses.find((c: any) => c.courseId === course.courseId);
                const materialCounts = countStudyMaterials(fullCourse?.courseDetails);
                const derivedProgress = calculateCourseProgress(fullCourse?.courseDetails);
                const progressPercentage =
                  typeof derivedProgress === "number"
                    ? derivedProgress
                    : course.progressPercentage || 0;
                const progressValue = Math.min(100, Math.max(0, Math.round(progressPercentage * 10) / 10));
                
                return (
                  <div key={course.courseId} className="card mb-3">
                    <div className="card-body">
                      <div className="row align-items-center">
                         <div className="col-md-12 d-flex flex-column flex-md-row align-items-center gap-3 mb-3">
                             <div className="col-12 col-md-2 text-center text-md-start">
                                 <div className="rounded overflow-hidden">
                                   <ThumbnailImage
                                       src={fullCourse?.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
                                       alt={course.courseTitle}
                                   />
                                 </div>
                             </div>
                             <div className="col-12 col-md-10">
                                 <h6 className="card-title mb-2">{course.courseTitle}</h6>
                                 <p className="text-muted small mb-3">{fullCourse?.courseDescription || 'No description available'}</p>
                             </div>
                         </div>
                        
                        {/* Course Materials - Same format as CourseDetailsModal */}
                        <div className="col-md-12">
                          <div className="row g-2 mb-3">
                           
                            <div className="col-md-4">
                              <div className="position-relative p-2 bg-light rounded">
                                <div className="d-flex align-items-center">
                                  <i className="ti ti-video text-primary me-2" style={{ fontSize: '16px' }}></i>
                                  <span className="fw-medium small">{materialCounts.videos} Video Lessons</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-md-4">
                              <div className="position-relative p-2 bg-light rounded">
                                <div className="d-flex align-items-center">
                                  <i className="ti ti-file-text text-info me-2" style={{ fontSize: '16px' }}></i>
                                  <span className="fw-medium small">{materialCounts.documents + materialCounts.links} Study Materials</span>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="position-relative p-2 bg-light rounded">
                                <div className="d-flex align-items-center">
                                  <i className="ti ti-clipboard-check text-success me-2" style={{fontSize: '16px'}}></i>
                                  <span className="fw-medium small">{fullCourse?.assessmentCount || 0} Tests</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Subjects Display */}
                          {fullCourse?.courseDetails?.subjects && fullCourse.courseDetails.subjects.length > 0 && (
                            <div className="mb-3">
                              <h6 className="mb-2 small">Course Subjects</h6>
                              <div className="row g-1">
                                {fullCourse.courseDetails.subjects.map((subject: any) => (
                                  <div key={subject.subjectId} className="col-md-6">
                                    <div className="p-2 border rounded">
                                      <div className="fw-medium small">{subject.subjectTitle}</div>
                                      <small className="text-muted">{subject.subjectDescription}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted">Progress</span>
                          <span className="fw-medium">{progressValue}%</span>
                        </div>
                        <div className="progress">
                          <div 
                            className="progress-bar bg-primary" 
                            role="progressbar" 
                            style={{ width: `${progressValue}%` }}
                            aria-valuenow={progressValue} 
                            aria-valuemin={0} 
                            aria-valuemax={100}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
              ) : (
                <div className="text-center py-5">
                  <i className="ti ti-book-off display-1 text-muted mb-3"></i>
                  <h5 className="text-muted">No Course Progress</h5>
                  <p className="text-muted">You haven't started any courses yet.</p>
                </div>
              )}
            </div>
          </div>
          {/* Footer */}

        </div>

              {/* Course Details Modal */}
              <CourseDetailsModal 
                course={selectedCourse} 
                onClose={() => setSelectedCourse(null)} 
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default MyAccount;